import React, { useState } from "react";
import type { Step, HubSpotProduct, ItemImage, ShowWhen, ExclusionCondition } from "../../types/configurator";
import { PlusIcon, BoxIcon } from "../ui/Icons";
import { StepCard } from "./StepCard";
import "./StepBuilder.css";

interface Props {
  steps: Step[];
  productMap: Record<string, HubSpotProduct>;
  itemImageMap: Record<string, ItemImage[]>;
  onAddStep: () => void;
  onDuplicateStep: (id: string) => void;
  onRemoveStep: (id: string) => void;
  onUpdateStep: (id: string, updates: Partial<Step>) => void;
  onReorderStep: (fromId: string, toId: string) => void;
  onAddProduct: (stepId: string, productId: string) => void;
  onAddFieldValue: (stepId: string, field: string, value: string, label: string) => void;
  onRemoveProduct: (stepId: string, itemUid: string) => void;
  onSetConditions: (stepId: string, conditions: ShowWhen[]) => void;
  onSetExclusions: (stepId: string, exclusions: ExclusionCondition[]) => void;
  onItemImageUpload: (itemKey: string, file: File) => Promise<void>;
  onItemImageDelete: (imageId: string, itemKey: string) => Promise<void>;
  getStepIndex: (id: string) => number;
  onSave: () => void;
  saving: boolean;
  saveStatus: 'idle' | 'saved' | 'error';
  onBack: () => void;
  flowName: string;
  onFlowNameChange: (name: string) => void;
}

export const StepBuilder: React.FC<Props> = ({
  steps,
  productMap,
  itemImageMap,
  onAddStep,
  onDuplicateStep,
  onRemoveStep,
  onUpdateStep,
  onReorderStep,
  onAddProduct,
  onAddFieldValue,
  onRemoveProduct,
  onSetConditions,
  onSetExclusions,
  onItemImageUpload,
  onItemImageDelete,
  getStepIndex,
  onSave,
  saving,
  saveStatus,
  onBack,
  flowName,
  onFlowNameChange,
}) => {
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(
    new Set(steps.map((s) => s.id))
  );
  const [dragOverStep, setDragOverStep] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  React.useEffect(() => {
    const onDragStart = () => setIsDragging(true);
    const onDragEnd = () => { setIsDragging(false); setDragOverStep(null); };
    document.addEventListener('dragstart', onDragStart);
    document.addEventListener('dragend', onDragEnd);
    return () => {
      document.removeEventListener('dragstart', onDragStart);
      document.removeEventListener('dragend', onDragEnd);
    };
  }, []);

  const toggleExpanded = (id: string) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allExpanded = steps.length > 0 && steps.every((s) => expandedSteps.has(s.id));

  const toggleAll = () => {
    setExpandedSteps(allExpanded ? new Set() : new Set(steps.map((s) => s.id)));
  };

  // Ensure newly added steps are expanded
  const expandedRef = React.useRef(expandedSteps);
  expandedRef.current = expandedSteps;
  React.useEffect(() => {
    const newIds = steps.filter((s) => !expandedRef.current.has(s.id) && s.items.length === 0);
    if (newIds.length > 0) {
      setExpandedSteps((prev) => {
        const next = new Set(prev);
        newIds.forEach((s) => next.add(s.id));
        return next;
      });
    }
  }, [steps.length]);

  // ─── Drag handlers ────────────────────────────────────────────────────
  const handleStepDragStart = (e: React.DragEvent, stepId: string) => {
    e.dataTransfer.setData("stepId", stepId);
    e.dataTransfer.setData("source", "step-reorder");
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => setDragOverStep(null);

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverStep(null);

    const source = e.dataTransfer.getData("source");
    if (source === "catalog") {
      const productId = e.dataTransfer.getData("productId");
      onAddProduct(targetId, productId);
      setExpandedSteps((prev) => new Set([...prev, targetId]));
    } else if (source === "field-value") {
      const field = e.dataTransfer.getData("fieldName");
      const value = e.dataTransfer.getData("fieldValue");
      const label = e.dataTransfer.getData("fieldLabel");
      onAddFieldValue(targetId, field, value, label);
      setExpandedSteps((prev) => new Set([...prev, targetId]));
    } else if (source === "step-reorder") {
      const fromId = e.dataTransfer.getData("stepId");
      onReorderStep(fromId, targetId);
    }
  };

  const handleDragOver = (e: React.DragEvent, stepId: string) => {
    e.preventDefault();
    setDragOverStep(stepId);
  };

  const handleDragLeave = () => setDragOverStep(null);

  // Exactly one step may own the pricing call. With two or more, each of them
  // re-runs /api/pricing with a different slice of the selections and the last
  // one to answer wins — which is what produced the wrong quote totals we saw.
  // Saving is blocked until the admin clears the extras.
  const pricingSteps = steps.filter((s) => s.calculatePricing);
  const pricingConflict = pricingSteps.length > 1;

  return (
    <section className="builder">
      <div className="builder-toolbar">
        <div className="toolbar-left">
          <button className="btn btn-sm" onClick={onBack} style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
            ← Flows
          </button>
          <input
            value={flowName}
            onChange={e => onFlowNameChange(e.target.value)}
            placeholder="Flow name…"
            className="toolbar-flow-name"
          />
        </div>
        <div className="toolbar-actions">
          <button className="btn" onClick={toggleAll}>
            {allExpanded ? "Collapse All" : "Expand All"}
          </button>
          <button className="btn btn-accent" onClick={onAddStep}>
            <PlusIcon /> Add Step
          </button>
          <button
            className={`btn btn-save${saveStatus === 'saved' ? ' btn-saved' : saveStatus === 'error' ? ' btn-error' : ''}`}
            onClick={onSave}
            disabled={saving || pricingConflict}
            title={pricingConflict ? 'Fix the pricing-step conflict before saving' : undefined}
          >
            {saving ? 'Saving…' : saveStatus === 'saved' ? '✓ Saved' : saveStatus === 'error' ? 'Error' : 'Save'}
          </button>
        </div>
      </div>

      {pricingConflict && (
        <div className="builder-warning" role="alert">
          <span className="builder-warning-icon">⚠</span>
          <div>
            <strong>
              “Calculate pricing at this step” is enabled on {pricingSteps.length} steps.
            </strong>{' '}
            Only one step per flow may calculate pricing. Uncheck it on all but one of:{' '}
            {pricingSteps
              .map((s) => `${getStepIndex(s.id) + 1}. ${s.title || 'Untitled step'}`)
              .join(', ')}
            . Saving is disabled until this is fixed.
          </div>
        </div>
      )}

      <div className="builder-canvas">
        {steps.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <BoxIcon />
            </div>
            <h3>No steps yet</h3>
            <p>
              Add your first step and drag HubSpot products into it to build your
              configurator flow.
            </p>
            <button className="btn btn-accent" style={{ marginTop: 16 }} onClick={onAddStep}>
              <PlusIcon /> Add First Step
            </button>
          </div>
        ) : (
          steps.map((step, idx) => (
            <StepCard
              key={step.id}
              step={step}
              index={idx}
              isExpanded={expandedSteps.has(step.id)}
              isDragOver={dragOverStep === step.id}
              isDragging={isDragging}
              priorSteps={steps.slice(0, idx)}
              productMap={productMap}
              itemImageMap={itemImageMap}
              allSteps={steps}
              onUpdate={onUpdateStep}
              onDuplicate={onDuplicateStep}
              onRemove={onRemoveStep}
              onToggleExpand={toggleExpanded}
              onRemoveProduct={onRemoveProduct}
              onSetConditions={onSetConditions}
              onSetExclusions={onSetExclusions}
              onItemImageUpload={onItemImageUpload}
              onItemImageDelete={onItemImageDelete}
              onDragStart={handleStepDragStart}
              onDragEnd={handleDragEnd}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              getStepIndex={getStepIndex}
              pricingConflict={pricingConflict && !!step.calculatePricing}
            />
          ))
        )}

        {steps.length > 0 && (
          <button className="add-step-btn" onClick={onAddStep}>
            <PlusIcon /> Add Step
          </button>
        )}
      </div>
    </section>
  );
};
